import React, { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Button,
  DialogModal,
  Drawer,
  PanelHeader,
  TextController,
  GroupedController,
  ReactSelectController,
  NumberController,
  TextAreaController,
} from "@pushpress/shared-components";
import { AppContext } from "../../../context/app";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { useAppointmentDiscountQuery } from "../../../apis/appointmentApi.hooks";
import {
  convertDiscountFormToPathPayload,
  convertDiscountFormToPostPayload,
  convertToDiscountForm,
} from "./DiscountDrawer.utils";
import { useMutation } from "@tanstack/react-query";
import {
  patchAppointmentDiscount,
  postAppointmentDiscount,
} from "../../../apis/appointmentApi";
import { getCurrencySymbol } from "../../../utils/currencyUtils";

export interface DiscountForm {
  name: string;
  discountType: {
    label: string;
    value: string;
  };
  discountValue: number;
  description?: string;
}

interface DiscountDrawerProps {
  appointmentDiscountUuid?: string;
  visible: boolean;
  duplicate?: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

function DiscountDrawer(props: DiscountDrawerProps) {
  const { visible } = props;

  return (
    <Drawer
      visible={visible}
      anchor="right"
      width="420px"
      disableClickAway
      disableEscapeKeyDown
    >
      <DiscountDrawerContent {...props} />
    </Drawer>
  );
}

function DiscountDrawerContent(props: DiscountDrawerProps) {
  const { appointmentDiscountUuid, duplicate, onClose, onSuccess } = props;

  const app = useContext(AppContext);

  const [saving, setSaving] = useState(false);

  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);

  const discountQuery = useAppointmentDiscountQuery(appointmentDiscountUuid!, {
    enabled: !!appointmentDiscountUuid,
  });

  const postAppointmentMutation = useMutation(postAppointmentDiscount);

  const patchAppointmentMutation = useMutation(patchAppointmentDiscount);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<DiscountForm>({
    mode: "onTouched",
  });

  const handleClose = () => {
    if (isDirty) {
      setConfirmCloseVisible(true);
    } else {
      onClose();
    }
  };

  const save = async (values: DiscountForm) => {
    try {
      setSaving(true);
      if (appointmentDiscountUuid && !duplicate) {
        const payload = convertDiscountFormToPathPayload(values);
        await patchAppointmentMutation.mutateAsync({
          clientUuid: app.client!.uuid,
          appointmentDiscountId: appointmentDiscountUuid,
          payload,
        });
      } else {
        const payload = convertDiscountFormToPostPayload(values);
        await postAppointmentMutation.mutateAsync({
          clientUuid: app.client!.uuid,
          payload,
        });
      }
      toast.success("Save successful", defaultToastOptions);
      onSuccess();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    handleSubmit(save)();
  };

  const discountTypeOptions = useMemo(
    () => [
      {
        label: "%",
        value: "PERCENTAGE",
      },
      {
        label: getCurrencySymbol(app.client!.currencyIso),
        value: "FLAT",
      },
    ],
    [app.client],
  );

  useEffect(() => {
    if (discountQuery.data?.data) {
      const values = convertToDiscountForm(
        discountQuery.data.data,
        duplicate,
        discountTypeOptions,
      );
      reset(values);
    }
  }, [discountQuery.data?.data, discountTypeOptions, duplicate, reset]);

  if (discountQuery.isLoading && discountQuery.isFetching) {
    return null;
  }

  return (
    <>
      <PanelHeader title="Discount">
        <PanelHeader.ActionMenu>
          <Button
            text="Close"
            buttonType="secondary"
            size="medium"
            onClick={handleClose}
          />
          <Button
            text="Save"
            buttonType="primary"
            size="medium"
            type="button"
            loading={saving}
            onClick={handleSave}
          />
        </PanelHeader.ActionMenu>
      </PanelHeader>
      <div style={{ padding: 24 }}>
        <form onSubmit={handleSubmit(save)}>
          <TextController control={control} name="name" label="Name" required />
          <GroupedController
            label="Amount"
            required
            flexItems={["0 0 86px", "1"]}
          >
            <ReactSelectController
              label="Option"
              control={control}
              name="discountType"
              options={discountTypeOptions}
              required
              menuPortalTarget={document.body}
            />
            <NumberController
              label="Value"
              control={control}
              name="discountValue"
              required
              min={1}
              maxDecimalDigits={2}
            />
          </GroupedController>
          <TextAreaController
            control={control}
            name="description"
            label="Description"
            required
          />
        </form>
      </div>
      <DialogModal
        title="Discard unsaved changes?"
        visible={confirmCloseVisible}
        primaryBtnTitle="Discard"
        secondaryBtnTitle="Go back"
        primaryHandler={onClose}
        secondaryHandler={() => setConfirmCloseVisible(false)}
        closeModal={() => setConfirmCloseVisible(false)}
      />
    </>
  );
}

export default DiscountDrawer;
